import * as React from 'react';

import styled, { css } from 'styled-components';

import { AnimatePresence } from 'framer-motion';

import { getImageAndMaskWithProductId } from '@webapp/utils/merch/imageAndMaskWithProductId';

import MotionOpacity from '@components/motion/motion-opacity';
import ImageColorFilter from '@components/image-color-filter';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';
import { darken, rgba } from 'polished';
import Flex from '@components/layout/flex';

interface MerchProductProps {
  className?: string;
  productImgSrc?: string;
  productId?: number;
  colorCode?: string;
  filtered?: boolean;
  children?: React.ReactNode;
  selected?: boolean;
  small?: boolean;
  onClick?: () => void;
  isOnColor?: boolean;
}

export const MerchProduct: React.FC<MerchProductProps> = ({
  className,
  children,
  small,
  colorCode,
  productId,
  productImgSrc,
  selected,
  filtered,
  onClick,
  isOnColor = false,
}) => {
  const imageAndMAsk = productId ? getImageAndMaskWithProductId(productId) : [];
  return (
    <ProductWrapper
      className={`${className} ${selected ? 'selected' : ''}`}
      onClick={() => {
        if (onClick) {
          onClick();
        }
      }}
      filtered={filtered}
    >
      <AnimatePresence>
        <MotionOpacity>
          <Flex className="mb-10" justifyContent="center" alignItems="center">
            {selected ? <CompletedIcon /> : <Spacer />}
          </Flex>
        </MotionOpacity>
      </AnimatePresence>
      {filtered ? (
        <ImageColorFilter
          className="product-image"
          fill={colorCode || ''}
          imageSrc={imageAndMAsk[0] || ''}
          maskSrc={imageAndMAsk[1] || ''}
        />
      ) : (
        <ProductImg
          className={`product-image ${small ? 'small' : undefined}`}
          src={productImgSrc}
        />
      )}
      {children && <StyledName isOnColor={isOnColor}>{children}</StyledName>}
    </ProductWrapper>
  );
};

export default MerchProduct;

const StyledName = styled.div<{ isOnColor?: boolean }>`
  ${({ isOnColor }) =>
    isOnColor
      ? css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 42px;
          overflow: hidden;

          & > span {
            display: -webkit-inline-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
          }
        `
      : null}

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
interface ProductWrapperInterface {
  filtered?: boolean;
}

const ProductWrapper = styled.div<ProductWrapperInterface>`
  position: relative;
  background-color: ${({ theme }) => theme.colors.box};
  padding: 20px;
  cursor: pointer;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover,
  &.selected {
    background-color: ${({ theme }) => darken(0.05, theme.colors.box)};
  }

  ${({ filtered }) =>
    filtered
      ? css`
          background-color: ${({ theme }) =>
            rgba(theme.colors.differentBackground, 0.5)};
          transition: background-color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`};

          &:after {
            content: '';
            width: 100%;
            position: absolute;
            right: 0;
            bottom: 0;
            left: 0;
            height: 2px;
            opacity: 0;
            background-color: ${({ theme }) => theme.colors.primary};
            transition: opacity
              ${({ theme }) =>
                `${theme.transitions.duration} ${theme.transitions.type}`};
          }

          .product-image {
            opacity: 0.5;
            transition: opacity
              ${({ theme }) =>
                `${theme.transitions.duration} ${theme.transitions.type}`};
          }

          &:hover,
          &.selected {
            background-color: ${({ theme }) =>
              theme.colors.differentBackground};

            .product-image {
              opacity: 1;
            }
          }

          &.selected {
            &:after {
              opacity: 1;
            }
          }
        `
      : css`
          &:nth-last-child(1) {
            border-radius: 0 0 4px 4px;
          }

          @media ${({ theme }) => theme.device.tablet} {
            &:nth-last-child(2) {
              border-radius: 0 0 0 4px;
            }

            &:nth-last-child(1) {
              border-radius: 0 0 4px 0;
            }
          }
        `}
`;

const ProductImg = styled.img`
  display: block;
  width: 100%;
  object-fit: cover;
  margin: 0 auto 20px;
  @media ${({ theme }) => theme.device.tablet} {
    max-width: 220px;
    max-height: 220px;
  }
`;

const Spacer = styled.span`
  width: 24px;
  height: 24px;
  display: block;
`;
